import moment from 'moment';
import React, { FormEvent, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import CollapsibleSection from 'src/components/common/CollapsibleSection';
import Dialog, { DialogActionBar } from 'src/components/common/Dialog.redesign';
import Filter from 'src/components/common/Filter';
import { ITableConfig } from 'src/components/common/table';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import PagedResultSet from 'src/models/common/PagedResultSet';
import LeaseRecording from 'src/models/dlcLease/LeaseRecording';
import Transmittal from 'src/models/dlcLease/Transmittal';
import { IRootState } from 'src/reducers';
import { ODataOptions } from 'src/util/ODataBuilder';
import useFetch from 'use-http';
import LeaseRecordingsList from './LeaseRecordingsList';
import Button from 'src/components/controls/forms/redesign/FormButton';
import LoadingSpinner from 'src/components/common/LoadingSpinner';

interface ITransmittalDialogStateProps {
    transmittal: Transmittal;

    sendUpdate: () => void;
    onCloseDialog?: () => void;
}

const TransmittalDialog = (props: ITransmittalDialogStateProps) => {
    const parentListTableConfig: ITableConfig = {
        rowCount: 15,
        pageNumber: 1,
        idField: 'id',
        columns: [{ name: 'Number', mapping: 'id' }],
    };

    const transmittalTypeOptions = [{ value: 'L', label: 'Lease' }, { value: 'S', label: 'Permit' }, { value: 'O', label: 'Lease Option' }];

    const { register, errors, reset, handleSubmit } = useForm();
    const { get, post, put, response } = useFetch();
    const appContext = useSelector((state: IRootState) => state.appContext);

    const [selectList, setSelectList] = useState<number[]>([]);
    const [pLeaseRecordings, setPLeaseRecordings] = useState<PagedResultSet<LeaseRecording>>(null);

    // Filter props
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [filterOptions, setFilterOptions] = useState<ODataOptions>(ODataOptions.from(parentListTableConfig));

    useEffect(() => {
        if (!!props.transmittal) {
            reset(props.transmittal);
            if (props.transmittal.leaseRecordings?.length > 0) {
                let recIds = props.transmittal.leaseRecordings.map(lr => lr.lr_tg);
                setSelectList(recIds);
            }
        }
    }, []);

    useEffect(() => {
        getLeaseRecordings();
    }, [filterOptions]);
    
    // FETCH CALLS
    const getLeaseRecordings = async () => {
        let result = await get(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/processingMaintenance/lease-recording${filterOptions.toString()}`);

        if (response.ok) {
            let newRecordings = result;
            if (pageNumber > 1) {
                newRecordings.items = [...pLeaseRecordings.items].concat(result.items);
            }
            setPLeaseRecordings(newRecordings);
        } else {
            console.log('getLeaseRecordings Error:', result);
            window.alert('Error getting Lease Recordings');
        }
    };

    const createTransmittal = async (transmittal: Transmittal) => {
        let result = await post(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/processingMaintenance/transmittal`, transmittal);

        if (response.ok) {
            props.sendUpdate();
            props.onCloseDialog();
        } else {
            console.log('createTransmittal Error:', result);
            window.alert('Error creating Transmittal');
        }
    };

    const updateTransmittal = async (transmittal: Transmittal) => {
        let result = await put(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/processingMaintenance/transmittal`, transmittal);

        if (response.ok) {
            props.sendUpdate();
            props.onCloseDialog();
        } else {
            console.log('updateLeaseRecording Error:', result);
            window.alert('Error updating Transmittal');
        }
    };

    // SUBMISSION LOGIC
    const onSubmit = (data: Transmittal) => {
        if (selectList.length > 0) {
            data = finalizeData(data);
            if (data.transmit_no > 0) {
                updateTransmittal(data);
            } else {
                createTransmittal(data);
            }
        } else {
            window.alert('At least one lease record must be attached!');
        }
    };

    const finalizeData = (data: Transmittal) => {
        data.transmit_no = props.transmittal ? props.transmittal.transmit_no : 0;

        data.doc_date = data.doc_date ? (moment(data.doc_date).format('YYYY-MM-DD') as unknown as Date) : null;

        let oldList = props.transmittal?.leaseRecordings ?? [];
        data.detachRecordings = oldList.filter(ol => selectList.indexOf(ol.lr_tg) === -1);
        data.leaseRecordings = pLeaseRecordings?.items?.filter(pl => selectList.indexOf(pl.lr_tg) > -1);

        return data;
    };

    // FORM LOGIC
    const onIncrementPage = () => {
        if (pageNumber * pLeaseRecordings.pageSize < pLeaseRecordings.totalRecords) {
            let pConfig = parentListTableConfig;
            pConfig.pageNumber = pageNumber + 1;
            
            let initOptions = ODataOptions.from(pConfig);
            
            initOptions.merge(initOptions);
            setPageNumber(pConfig.pageNumber);
            setFilterOptions(initOptions);
        }
    };

    const handleRecordingSelect = (e: FormEvent<HTMLInputElement>, recordingId: number) => {
        let newList = [...selectList];

        let record = newList.indexOf(recordingId);

        if (e.currentTarget.checked === true && record === -1) {
            newList.push(recordingId);
        } else if (e.currentTarget.checked !== true && record > -1) {
            newList.splice(record, 1);
        }

        setSelectList(newList);
    };

    const handleFilterChanged = (options: ODataOptions) => {
        setPageNumber(1);
        let initOptions = ODataOptions.from(parentListTableConfig);

        initOptions.merge(options);
        setFilterOptions(initOptions);
    };

    return (
        <Dialog title="Transmittals">
            <div className="mb-1 mt-1" style={{ width: '85vw' }}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div style={{ height: '80vh', marginBottom: '1rem', overflow: 'auto' }}>
                        <CollapsibleSection title="Transmittal Data">
                            <div style={{ display: 'flex', justifyContent: 'space-around', rowGap: '1rem' }}>
                                <RHFInputBox
                                    name="transmit_no"
                                    label="Transmit no"
                                    register={register}
                                    errors={errors}
                                    disabled={true}
                                    bodyStyle={{ width: '10%' }}
                                    style={{ width: '100%' }}
                                />

                                <RHFSelect
                                    name="doc_type"
                                    label="Doc. Type"
                                    options={transmittalTypeOptions}
                                    register={register}
                                    errors={errors}
                                    bodyStyle={{ width: '10%' }}
                                    style={{ width: '100%' }}
                                />

                                <RHFInputBox
                                    name="doc_date"
                                    label="Doc. Date"
                                    register={register}
                                    errors={errors}
                                    type="date"
                                    bodyStyle={{ width: '10%' }}
                                    style={{ width: '100%' }}
                                />

                                <RHFInputBox
                                    name="notary"
                                    label="Notary"
                                    register={register}
                                    errors={errors}
                                    bodyStyle={{ width: '10%' }}
                                    style={{ width: '100%' }}
                                />

                                <RHFInputBox
                                    name="phone"
                                    label="Phone"
                                    register={register}
                                    errors={errors}
                                    bodyStyle={{ width: '10%' }}
                                    style={{ width: '100%' }}
                                />
                            </div>
                        </CollapsibleSection>
                        <CollapsibleSection title="Lease Recordings" startCollapsed={true}>
                            <>
                                <div>
                                    <div className="dlc-content-panel-header">Recorded Leases</div>
                                    <Filter
                                        sortItems={[
                                            { group: 1, text: 'Lease No', icon: 'alpha-down', orderName: 'lm.LS_NO', orderDirection: 'descending' },
                                            { group: 1, text: 'Invoice No', icon: 'alpha-down', orderName: 'li.INVOICE_NO1', orderDirection: 'descending' },
                                            { group: 1, text: 'Transmit No', icon: 'alpha-down', orderName: 'lr.TRANSMIT_NO', orderDirection: 'descending' },
                                            { group: 1, text: 'Lessor Name', icon: 'alpha-down', orderName: 'll.NAME1', orderDirection: 'descending' },
                                        ]}
                                        filterItems={[
                                            { group: 1, text: 'Lease No', orderName: 'lease_no', orderDirection: 'descending' },
                                            { group: 1, text: 'Invoice No', orderName: 'invoice_no', orderDirection: 'descending' },
                                            { group: 1, text: 'Transmit No', orderName: 'transmit_no', orderDirection: 'descending' },
                                            { group: 1, text: 'Lessor Name', orderName: 'lessor_name', orderDirection: 'descending' },
                                        ]}
                                        onFilterChanged={handleFilterChanged}
                                    />
                                </div>
                                {pLeaseRecordings?.items?.length > 0 && (
                                    <LeaseRecordingsList
                                        leaseRecordings={pLeaseRecordings}
                                        forBatching={true}
                                        isAssigned={false}
                                        selectList={selectList}
                                        updateSelectList={handleRecordingSelect}
                                        onUpdateLeaseRecording={props.sendUpdate}
                                        incrementPage={onIncrementPage}
                                    />
                                )}
                                {pLeaseRecordings?.items?.length === 0 && <LoadingSpinner />}
                            </>
                        </CollapsibleSection>
                    </div>
                    <DialogActionBar>
                        <Button
                            intent="primary"
                            type="submit"
                            text="Save"
                        />
                        <Button intent="danger" onClick={() => props.onCloseDialog()} text="Cancel" />
                    </DialogActionBar>
                </form>
            </div>
        </Dialog>
    );
};

export default TransmittalDialog;


// WEBPACK FOOTER //
// ./src/components/project/sections/TractComponents/ProcessingAndMaintenance/TransmittalDialog.tsx